import { Button, Spinner, useTranslation } from '@hbs-us/ui';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { type FormEvent, useEffect, useMemo, useState } from 'react';

import type { InventoryFieldDefinitionDto } from './contractsMirror.js';
import {
  useCreateInventoryItemMutation,
  useEditInventoryItemMutation,
  useInventoryQuery,
} from './useInventoryQuery.js';

const INPUT_CLASS =
  'h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const TEXTAREA_CLASS =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const LABEL_CLASS = 'mb-1 block font-medium text-foreground text-sm';

function parseSelectOptions(def: InventoryFieldDefinitionDto): string[] {
  if (!def.options) return [];
  try {
    const parsed = JSON.parse(def.options) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (item): item is string => typeof item === 'string' && item.trim().length > 0,
      );
    }
  } catch {
    // Fall through to a line-based fallback if options are not JSON.
  }
  return def.options
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export interface InventoryItemFormRouteProps {
  mode: 'create' | 'edit';
}

export function InventoryItemFormRoute({ mode }: InventoryItemFormRouteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEdit = mode === 'edit';
  // Read the `$id` param in edit mode (strict:false is safe under both the edit + create routes).
  const params = useParams({ strict: false }) as { id?: string };
  const editItemId = isEdit ? Number(params.id) : null;

  // Edit resolves the item (and its category defs) from the coarse query — include archived so an
  // archived item still resolves. Create only needs the active category list.
  const inventoryQuery = useInventoryQuery(isEdit ? { archived: true } : {});
  const createMutation = useCreateInventoryItemMutation();
  const editMutation = useEditInventoryItemMutation();

  const [categoryId, setCategoryId] = useState<string>('');
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('0');
  const [quantityUnit, setQuantityUnit] = useState('');
  const [notes, setNotes] = useState('');
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [prefilled, setPrefilled] = useState(false);

  // The item under edit (edit mode only).
  const editItem = useMemo(() => {
    if (!isEdit || !editItemId) return undefined;
    return (inventoryQuery.data?.items ?? []).find((item) => item.id === editItemId);
  }, [isEdit, editItemId, inventoryQuery.data?.items]);

  const categories = useMemo(
    () =>
      (inventoryQuery.data?.categories ?? []).filter((category) => category.archivedAt === null),
    [inventoryQuery.data?.categories],
  );

  const selectedCategory = useMemo(() => {
    // In edit mode the category is locked to the item's category (which may itself be archived, so
    // resolve it from the FULL category list, not the active-only one).
    if (isEdit && editItem) {
      return (inventoryQuery.data?.categories ?? []).find((c) => c.id === editItem.categoryId);
    }
    const id = Number(categoryId);
    return categories.find((category) => category.id === id);
  }, [isEdit, editItem, inventoryQuery.data?.categories, categories, categoryId]);

  // Create: default the category to the first active one. Edit: prefill every field once from the
  // resolved item (guarded by `prefilled` so user edits are never clobbered on refetch).
  useEffect(() => {
    if (isEdit) {
      if (editItem && !prefilled) {
        setCategoryId(String(editItem.categoryId));
        setName(editItem.name);
        setQuantity(String(editItem.quantity));
        setQuantityUnit(editItem.quantityUnit ?? '');
        setNotes(editItem.notes ?? '');
        setCustomFieldValues({ ...editItem.customFields });
        setPrefilled(true);
      }
      return;
    }
    if (!categoryId && categories.length > 0) {
      setCategoryId(String(categories[0].id));
    }
  }, [isEdit, editItem, prefilled, categories, categoryId]);

  if (inventoryQuery.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (inventoryQuery.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('inventory:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('inventory:error.body')}</p>
        <button
          type="button"
          onClick={() => void inventoryQuery.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('inventory:error.retry')}
        </button>
      </section>
    );
  }

  // Edit mode: the item id did not resolve to an item → announced not-found with a back link.
  if (isEdit && !editItem) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('inventory:error.detailHeading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('inventory:error.detailBody')}</p>
        <Button asChild variant="secondary">
          <Link to="/inventory">{t('inventory:actions.backToItems')}</Link>
        </Button>
      </section>
    );
  }

  const noCategories = categories.length === 0;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedCategory) return;

    const customFields: Record<string, string | number> = {};
    for (const def of selectedCategory.fieldDefinitions) {
      const rawValue = (customFieldValues[def.fieldKey] ?? '').trim();
      if (!rawValue) continue;
      if (def.fieldType === 'number') {
        const numeric = Number(rawValue);
        customFields[def.fieldKey] = Number.isFinite(numeric) ? numeric : rawValue;
      } else {
        customFields[def.fieldKey] = rawValue;
      }
    }

    setSuccessMessage(null);

    if (isEdit && editItemId) {
      editMutation.mutate(
        {
          itemId: editItemId,
          name: name.trim(),
          quantity: Number(quantity),
          quantityUnit: quantityUnit.trim() || undefined,
          notes: notes.trim() || undefined,
          customFields,
        },
        {
          onSuccess: () => {
            setSuccessMessage(t('inventory:items.form.updateSuccess'));
            setTimeout(() => {
              void navigate({ to: '/inventory/$id', params: { id: String(editItemId) } });
            }, 200);
          },
        },
      );
      return;
    }

    createMutation.mutate(
      {
        categoryId: selectedCategory.id,
        name: name.trim(),
        quantity: Number(quantity),
        quantityUnit: quantityUnit.trim() || undefined,
        notes: notes.trim() || undefined,
        customFields,
      },
      {
        onSuccess: () => {
          setSuccessMessage(t('inventory:items.form.createSuccess'));
          setTimeout(() => {
            void navigate({ to: '/inventory' });
          }, 200);
        },
      },
    );
  }

  const activeMutation = isEdit ? editMutation : createMutation;

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl text-foreground">
          {isEdit ? t('inventory:items.form.editHeading') : t('inventory:items.form.createHeading')}
        </h1>
      </header>

      {!isEdit && noCategories ? (
        <div className="max-w-2xl rounded-md border border-border bg-card p-6">
          <p className="font-medium text-foreground text-sm">
            {t('inventory:items.form.noCategories')}
          </p>
          <p className="mt-2 text-muted-foreground text-sm">
            {t('inventory:items.form.noCategoriesHelp')}
          </p>
          <div className="mt-4">
            <Button asChild variant="secondary">
              <Link to="/inventory/categories">{t('inventory:actions.manageCategories')}</Link>
            </Button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="max-w-3xl space-y-4 rounded-md border border-border bg-card p-6"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="item-category" className={LABEL_CLASS}>
                {t('inventory:items.form.category')}
              </label>
              {isEdit ? (
                // Category is LOCKED on edit — the SSR donor keeps the item in its category
                // (changing it would strand the item's custom_fields values against a new schema).
                <>
                  <select
                    id="item-category"
                    value={selectedCategory ? String(selectedCategory.id) : ''}
                    disabled
                    aria-readonly="true"
                    className={INPUT_CLASS}
                  >
                    {selectedCategory ? (
                      <option value={selectedCategory.id}>{selectedCategory.name}</option>
                    ) : null}
                  </select>
                  <p className="mt-1 text-muted-foreground text-xs">
                    {t('inventory:items.form.categoryLocked')}
                  </p>
                </>
              ) : (
                <select
                  id="item-category"
                  value={categoryId}
                  onChange={(event) => {
                    setCategoryId(event.target.value);
                    setCustomFieldValues({});
                  }}
                  required
                  className={INPUT_CLASS}
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="item-name" className={LABEL_CLASS}>
                {t('inventory:items.form.name')}
              </label>
              <input
                id="item-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={200}
                required
                className={INPUT_CLASS}
              />
            </div>

            <div>
              <label htmlFor="item-quantity" className={LABEL_CLASS}>
                {t('inventory:items.form.quantity')}
              </label>
              <input
                id="item-quantity"
                type="number"
                min="0"
                step="any"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                required
                className={INPUT_CLASS}
              />
            </div>

            <div>
              <label htmlFor="item-quantity-unit" className={LABEL_CLASS}>
                {t('inventory:items.form.quantityUnit')}
              </label>
              <input
                id="item-quantity-unit"
                value={quantityUnit}
                onChange={(event) => setQuantityUnit(event.target.value)}
                className={INPUT_CLASS}
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="item-notes" className={LABEL_CLASS}>
                {t('inventory:items.form.notes')}
              </label>
              <textarea
                id="item-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={4}
                className={TEXTAREA_CLASS}
              />
            </div>
          </div>

          {selectedCategory && selectedCategory.fieldDefinitions.length > 0 ? (
            <section className="rounded-md border border-border/60 p-4">
              <h2 className="mb-3 font-display font-semibold text-foreground text-lg">
                {t('inventory:items.form.customFieldsHeading', { category: selectedCategory.name })}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {selectedCategory.fieldDefinitions.map((def) => {
                  const fieldId = `custom-field-${def.id}`;
                  const value = customFieldValues[def.fieldKey] ?? '';
                  const required = Boolean(def.required);

                  if (def.fieldType === 'textarea') {
                    return (
                      <div key={def.id} className="sm:col-span-2">
                        <label htmlFor={fieldId} className={LABEL_CLASS}>
                          {def.label}
                          {required ? ' *' : ''}
                        </label>
                        <textarea
                          id={fieldId}
                          value={value}
                          onChange={(event) =>
                            setCustomFieldValues((prev) => ({
                              ...prev,
                              [def.fieldKey]: event.target.value,
                            }))
                          }
                          required={required}
                          rows={3}
                          className={TEXTAREA_CLASS}
                        />
                      </div>
                    );
                  }

                  if (def.fieldType === 'select') {
                    const options = parseSelectOptions(def);
                    return (
                      <div key={def.id}>
                        <label htmlFor={fieldId} className={LABEL_CLASS}>
                          {def.label}
                          {required ? ' *' : ''}
                        </label>
                        <select
                          id={fieldId}
                          value={value}
                          onChange={(event) =>
                            setCustomFieldValues((prev) => ({
                              ...prev,
                              [def.fieldKey]: event.target.value,
                            }))
                          }
                          required={required}
                          className={INPUT_CLASS}
                        >
                          <option value="">
                            {t('inventory:items.form.selectOptionPlaceholder')}
                          </option>
                          {options.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  return (
                    <div key={def.id}>
                      <label htmlFor={fieldId} className={LABEL_CLASS}>
                        {def.label}
                        {required ? ' *' : ''}
                      </label>
                      <input
                        id={fieldId}
                        type={def.fieldType === 'number' ? 'number' : 'text'}
                        step={def.fieldType === 'number' ? 'any' : undefined}
                        value={value}
                        onChange={(event) =>
                          setCustomFieldValues((prev) => ({
                            ...prev,
                            [def.fieldKey]: event.target.value,
                          }))
                        }
                        required={required}
                        className={INPUT_CLASS}
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}

          {activeMutation.isError ? (
            <p role="alert" className="text-destructive text-sm">
              {isEdit
                ? t('inventory:items.form.updateError')
                : t('inventory:items.form.createError')}
            </p>
          ) : null}
          {successMessage ? (
            <p role="status" className="text-secondary-foreground text-sm">
              {successMessage}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={activeMutation.isPending}>
              {isEdit
                ? t('inventory:items.form.submitEdit')
                : t('inventory:items.form.submitCreate')}
            </Button>
            <Button asChild variant="secondary">
              <Link to="/inventory">{t('inventory:items.form.cancel')}</Link>
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
